import React from "react";
import defaultTheme, { useTheme } from "theme";
import styled, { css, keyframes } from "styled-components";
import {
  recapChartTypes,
  shortFormatNumberWithCurrency,
  RECAP_CELL_CATEGORY_TYPE_TEXT,
  recapChartOptions,
  formatNumberWithKuberaNumberFormatSettingsSelector
} from "@kubera/common";
import { useSelector, useStore } from "react-redux";
import { currentPortfolioCurrencySelector, convertCurrency } from "@kubera/common";
import ExpandIcon from "assets/images/exit.svg?react";
import ShowMoreIcon from "assets/images/showmore.svg?react";
import { routes } from "routes";
import ChangeLabel from "components/labels/ChangeLabel";
// import BadgeTip from "components/tooltip/BadgeTip";
import ChartsIcon from "assets/images/report_chart_icon.svg?react";
import expandableIndicatorIcon from "assets/images/expandable_indicator.svg";
import expandableIndicatorIconDark from "assets/images/expandable_indicator_dark.svg";
import i18n from "i18next";

const CellContainer = styled.div.attrs(props => ({
  tabIndex: 0
}))`
  display: flex;
  flex: 1;
  &:focus {
    outline: ${props => (!props.isGridHeader ? `2px solid ${props.theme.gridCellSelectedOutlineColor}` : "none")};
    outline-offset: -2px;
  }
  min-width: ${props =>
    props.isTextCell ? "450px" : props.isChartsCell ? "28px" : props.isExpandCollapseIconCell ? "20px" : "155px"};
  max-width: ${props =>
    props.isTextCell ? "none" : props.isChartsCell ? "28px" : props.isExpandCollapseIconCell ? "20px" : "170px"};
  width: auto;
  align-items: center;
  position: -webkit-sticky;
  position: ${props =>
    (props.isTextCell || props.isChartsCell || props.isExpandCollapseIconCell) && props.isExpandedView
      ? "sticky"
      : "static"};
  z-index: ${props => (props.isTextCell || props.isChartsCell ? 5 : props.isExpandCollapseIconCell ? 10 : 0)};
  left: ${props => (props.isExpandCollapseIconCell ? "60px" : props.isChartsCell ? "80px" : "108px")};
  //left: 108px
  background-color: ${props =>
    props.isGridHeader ? props.theme.dashboardBackgroundColor : props.theme.gridRowUpdatedBackgroundColor};
  border-right: ${props =>
    (props.isLastCell && !props.isGridHeader) ||
    (props.showOverflowIndicator && props.isTextCell && props.isExpandedView)
      ? `1px solid ${props.theme.gridSectionBorderColor}`
      : "none"};
  border-left: ${props =>
    props.isFirstCell && !props.isGridHeader ? `1px solid ${props.theme.gridSectionBorderColor}` : "none"};
  box-sizing: border-box;
  cursor: ${props =>
    props.isChartsCell && !props.isExpandCollapseIconCell && !props.isDisabled && !props.isGridHeader
      ? "pointer"
      : "default"};
  scroll-margin-left: 590px;
  scroll-margin-right: 100px;
  -webkit-scroll-margin-left: 550px;
  &:hover {
    background-color: ${props =>
      (props.isChartsCell || props.isExpandCollapseIconCell) && !props.isDisabled && !props.isGridHeader
        ? props.theme.hoverRecapChartBgColor
        : ""};
    opacity: 1;
  }

  @media print {
    ${props => css`
      min-width: ${props.isTextCell && "0"};
      background-color: ${props.isGridHeader
        ? defaultTheme.dashboardBackgroundColor
        : defaultTheme.gridRowUpdatedBackgroundColor};
      border-right: ${(props.isLastCell && !props.isGridHeader) ||
      (props.showOverflowIndicator && props.isTextCell && props.isExpandedView)
        ? `1px solid ${defaultTheme.gridSectionBorderColor}`
        : "none"};
      border-left: ${props =>
        props.isFirstCell && !props.isGridHeader ? `1px solid ${defaultTheme.gridSectionBorderColor}` : "none"};
    `};
  }
`;

const CellItem = styled.div`
  // min-height: 40px;
  display: inline-flex;
  flex: 1;
  align-items: ${props => (props.isTextCell ? "flex-start" : "flex-end")};
  flex-direction: column;
  padding: 10px 0px 10px 15px;
  margin-right: ${props =>
    props.isLastCell
      ? props.isGridHeader
        ? props.isExpandedView
          ? props.shouldShowIcon
            ? "5px"
            : "36px"
          : "4px"
        : "36px"
      : "25px"};
`;

const CellPrimaryValue = styled.div`
  ${props =>
    props.isExpandedView &&
    props.isTextCell &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    `}
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: ${props => (props.isTextCell ? "400px" : "120px")};
  font-style: normal;
  font-size: ${props =>
    props.isGridHeader
      ? "11px"
      : (props.isNetWorthRow || props.category === "categoryHeader") && props.isTextCell && !props.isArchived
      ? "18px"
      : props.isTextCell && (props.isSectionHeader || props.category === "sheetHeader")
      ? "16px"
      : "14px"};
  font-feature-settings: "ss01" on;
  font-weight: ${props =>
    props.isGridHeader
      ? "700"
      : props.isTextCell &&
        (props.isNetWorthRow ||
          props.isSectionHeader ||
          props.category === "sheetHeader" ||
          props.category === "sectionHeader")
      ? "700"
      : "400"};
  text-align: ${props => (props.isTextCell ? "left" : "right")};
  // margin-bottom: 2px;
  white-space: ${props => (props.isTextCell ? "normal" : "nowrap")};
  text-transform: ${props => (props.isGridHeader ? "uppercase" : "none")};

  @media print {
    margin-left: -30px;
  }
`;

const RecapChangeLabel = styled(ChangeLabel)`
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: ${props => (props.isTextCell ? "400px" : "120px")};
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 13px;
  text-align: ${props => (props.isTextCell ? "left" : "right")};
  font-feature-settings: "ss01" on, "calt" off;

  @media print and (orientation: portrait) {
    max-width: ${props => (props.isTextCell ? "400px" : "102px")};
  }
`;
const CellSecondaryValue = styled.div`
  margin-top: ${props => (props.isTextCell ? "none" : "2px")};
`;
const EmptySecondaryValue = styled.div`
  color: ${props => props.theme.columnanChartSecondaryValueCLR};
  font-size: 11px;
  line-height: 13px;
`;
const ExpandColumnarChartIconContainer = styled.div`
  width: 32px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background-color: ${props => props.theme.focusBackgroundColor};
  }
  &:focus {
    background-color: ${props => props.theme.focusBackgroundColor};
  }

  @media print {
    visibility: hidden;
  }
`;
const ExpandColumnarChartIcon = styled(ExpandIcon)`
  height: 9px;
  width: 9px;
  margin-right: 10px;
  margin-left: 10px;
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;
const ShowMoreIconContainer = styled.div`
  height: 30px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: ${props => props.isexpandicondisabled && "none"};

  &:hover {
    background-color: ${props => props.theme.focusBackgroundColor};
  }
  &:focus {
    background-color: ${props => props.theme.focusBackgroundColor};
  }

  @media print {
    visibility: hidden;
  }
`;
const ShowMoreColumnarChartIcon = styled(ShowMoreIcon)`
  cursor: pointer;
  padding-right: 4px;
  padding-left: 1px;
  path {
    fill: ${props =>
      props.isexpandicondisabled ? props.theme.primaryButtonDisabledColor : props.theme.svgDefaultColor};
  }
`;

// const DetailsCellBadge = styled(BadgeTip)`
//   position: absolute;
//   right: 8px;
//   top: 11px;
// `;

const ChartsCell = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 36px;
  outline: 0;
  padding: 0;
  border: 0;
  margin: 0;
  cursor: ${props => (props.isDisabled ? "default" : "pointer")};
  background-color: transparent;

  &:focus {
    outline: 2px solid ${props => props.theme.gridCellSelectedOutlineColor};
    outline-offset: -1px;
  }

  @media print {
    display: none;
    width: 0;
  }
`;

const ChartsIconComponent = styled(ChartsIcon)`
  path {
    fill: ${props => (props.disabled ? props.theme.recapReportChartIconDisabled : props.theme.recapReportChartIcon)};
  }
`;

const ExpandCollapseIconCell = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 36px;
  outline: 0;
  padding: 0;
  border: 0;
  margin: 0;
  cursor: ${props => (props.isDisabled ? "default" : "pointer")};
  background-color: transparent;

  &:focus {
    outline: 2px solid ${props => props.theme.gridCellSelectedOutlineColor};
    outline-offset: -1px;
  }

  @media print {
    display: none;
    width: 0;
  }
`;

const ExpandSectionIcon = styled.div`
  flex : 1;
  width: 9px;
  height: 19px;
  //margin-left: 6px;
  background-color: transparent;
  background-image: url("${props =>
    props.theme.mode === "default" ? expandableIndicatorIcon : expandableIndicatorIconDark}");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 9px 9px;
  //transform: rotate(270deg);
  display: ${props => (!props.shouldShow ? "none" : "inline")} 
`;

const CollapseSectionIcon = styled.div`
  flex : 1;
  width: 9px;
  height: 19px;
  //margin-right: 3px;
  background-color: transparent;
  background-image: url("${props =>
    props.theme.mode === "default" ? expandableIndicatorIcon : expandableIndicatorIconDark}");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 9px 9px;
  transform: rotate(270deg);
  display: ${props => (!props.shouldShow ? "none" : "inline")} 
`;
const DescriptionForUnknownClass = styled.div`
  font-weight: normal;
  font-size: 11px;
  color: ${props => props.theme.columnanChartSecondaryValueCLR};
`;

const loadingAnimation = keyframes`
  50%   {opacity: 0.5;};
  75%  {opacity: 0.75;};
  100% {opacity: 1;};
`;
const LoadingSkeleton = styled.div`
  display: flex;
  height: 10px;
  border-radius: 20px;
  flex: 1;
  background-color: ${({ theme }) => theme.recapSkeletonLoaderColor};
  animation: ${loadingAnimation} 1s linear infinite;
`;
const CellLoadingSkeleton = styled(CellPrimaryValue)`
  color: ${({ theme }) => theme.recapSkeletonLoaderTextColor};
  animation: ${loadingAnimation} 1s linear infinite;
`;

const ColumnarChartCellComponent = props => {
  const theme = useTheme();
  const store = useStore();

  let {
    currency,
    cell,
    shouldShowIcon,
    isExpandIconDisabled,
    noOfColumns,
    setNoOfColumns,
    isGridHeader,
    isLastCell,
    isFirstCell,
    sectionIndex,
    rowIndex,
    showOverflowIndicator,
    isMoreColumnsAvailable,
    currentValue,
    previousValue,
    isExpandedView,
    cellId,
    category,
    isCollapsed,
    sectionName,
    cellIndex,
    maintainScrollStateOnGridChange = () => null
  } = props;
  currentValue = Math.kuberaFloor(currentValue);
  previousValue = previousValue === null ? previousValue : Math.kuberaFloor(previousValue);
  const portfolioCurrency = useSelector(currentPortfolioCurrencySelector);
  const isCurrencyCell = cell.selectedChartType === recapChartTypes.TOTALS;
  const isTextCell = cell.isTextCell || cell.type === RECAP_CELL_CATEGORY_TYPE_TEXT;
  const isChartsCell = cell.isChartsCell;
  const isExpandCollapseIconCell = cell.isExpandCollapseIconCell;
  const isDisabled = cell.isDisabled;
  const isPercentageChangeShown = cell.isPercentageChangeShown;
  const isSectionHeader = rowIndex === 0;
  const isDebtSection =
    (sectionIndex === 2 && cell.selectedChartOptions === recapChartOptions.NETWORTH.id) ||
    (cell.selectedChartOptions === recapChartOptions.SHEETS_AND_SECTIONS.id && sectionName === "Debts");
  const isNetWorthRow = sectionIndex === 0 && rowIndex === 0;
  const isPercentageCell = cell.selectedChartType === recapChartTypes.PERCENTAGE_ALLOCATION;
  const isArchived = cell.isArchived;
  const shouldShowExpandCollapseIcon =
    category === "header" || category === "sheetHeader" || category === "sectionHeader";
  const canShowPercentageChange = previousValue !== null && currentValue !== previousValue;

  const getFormattedValue = (
    value,
    currency,
    portfolioCurrency,
    includeCurrencySymbol,
    shouldIncludePositiveSign,
    alwaysShowDecimal
  ) => {
    if ((isCurrencyCell || isNetWorthRow || isSectionHeader) && !cell.isTextCell) {
      if (value === 0 && !isSectionHeader && cell.selectedChartOptions !== recapChartOptions.SHEETS_AND_SECTIONS.id) {
        return `---`;
      }
      const convertedCurrency = convertCurrency(store.getState(), value, currency, portfolioCurrency);
      return shortFormatNumberWithCurrency(
        store.getState(),
        convertedCurrency,
        portfolioCurrency,
        true,
        includeCurrencySymbol,
        shouldIncludePositiveSign,
        alwaysShowDecimal
      );
    } else {
      return isArchived ? `${value} (${i18n.t("archived")})` : value;
    }
  };
  const formattedValue = getFormattedValue(cell.value, currency, portfolioCurrency, isTextCell ? false : true, false);
  const convertedCurrentValue = convertCurrency(store.getState(), currentValue, currency, portfolioCurrency);
  const convertedPreviousValue = convertCurrency(store.getState(), previousValue, currency, portfolioCurrency);

  const showMoreColumns = () => {
    maintainScrollStateOnGridChange();
    setNoOfColumns(noOfColumns + 3);
  };

  const handleMoreColumnsBtnMouseDown = e => {
    e.preventDefault();
  };

  const expandRecapTabInNewWindow = () => {
    window.kuberaOpen(`${process.env.PUBLIC_URL}${routes.REPORTS_EXPANDED}`);
  };

  const showExpandOrShowMoreIcon = () => {
    if (shouldShowIcon) {
      if (isExpandedView) {
        if (isMoreColumnsAvailable) {
          return (
            <ShowMoreIconContainer
              onClick={showMoreColumns}
              onMouseDown={handleMoreColumnsBtnMouseDown}
              isexpandicondisabled={isExpandIconDisabled}
            >
              <ShowMoreColumnarChartIcon isexpandicondisabled={isExpandIconDisabled} />
            </ShowMoreIconContainer>
          );
        }
      } else {
        return (
          <ExpandColumnarChartIconContainer onClick={expandRecapTabInNewWindow}>
            <ExpandColumnarChartIcon />
          </ExpandColumnarChartIconContainer>
        );
      }
    }
  };
  const showCellSecondaryValue = isDebtSection => {
    if ((isCurrencyCell || isPercentageCell) && isPercentageChangeShown) {
      if (canShowPercentageChange) {
        return (
          <RecapChangeLabel
            startValue={isPercentageCell ? previousValue : convertedPreviousValue}
            endValue={isPercentageCell ? currentValue : convertedCurrentValue}
            currency={portfolioCurrency}
            isChangeCalculatedForPercentageValues={
              isPercentageCell &&
              (!isSectionHeader ||
                (cell.selectedChartOptions !== recapChartOptions.CASH_ON_HAND.id &&
                  cell.selectedChartOptions !== recapChartOptions.NETWORTH.id)) &&
              !isNetWorthRow
                ? true
                : false
            }
            disableColor={isDebtSection}
            disableColorCode={theme.gridCellSecondaryColor}
            shouldShowCurrencySymbol={false}
            showPercentageInsideParanthesis={false}
            isRecapChangeLabel={true}
          />
        );
      } else {
        if (cell.value === 0) {
          return null;
        }
        return <EmptySecondaryValue>----</EmptySecondaryValue>;
      }
    } else if (
      isTextCell &&
      category === "header" &&
      (cell.selectedChartOptions === recapChartOptions.ASSET_CLASSES.id ||
        cell.selectedChartOptions === recapChartOptions.INVESTABLE.id ||
        cell.selectedChartOptions === recapChartOptions.INVESTABLE_WITHOUT_CASH.id) &&
      cell.value === "Unknown"
    ) {
      return <DescriptionForUnknownClass>{i18n.t("unknownAssetClassDescription.recap")}</DescriptionForUnknownClass>;
    }
  };
  const handleFocus = e => {
    //selectText(cellId);
  };

  const handleKeyDown = e => {
    if (e.target.id !== cellId) {
      return false;
    }
    if (e.key === "Enter") {
      if (!isDisabled && isChartsCell) {
        console.log("charts modal");
        props.handleOpenChartsModalClick(e);
      }
    }
  };

  const renderCellContent = () => {
    if (cell.isDummyCell) {
      if (isTextCell) {
        return <LoadingSkeleton />;
      } else {
        return (
          <CellItem>
            <CellLoadingSkeleton>{"XXXX"}</CellLoadingSkeleton>
          </CellItem>
        );
      }
    } else if (isChartsCell && !isGridHeader) {
      return (
        <ChartsCell isDisabled={isDisabled}>
          <ChartsIconComponent disabled={isDisabled} />
        </ChartsCell>
      );
    } else if (isExpandCollapseIconCell) {
      return (
        <ExpandCollapseIconCell isDisabled={isDisabled}>
          {!isCollapsed
            ? !isDisabled && <ExpandSectionIcon shouldShow={shouldShowExpandCollapseIcon} />
            : !isDisabled && <CollapseSectionIcon shouldShow={shouldShowExpandCollapseIcon} />}
        </ExpandCollapseIconCell>
      );
    } else {
      const secondaryValue = showCellSecondaryValue(isDebtSection);
      return (
        <CellItem
          isTextCell={isTextCell}
          isExpandedView={isExpandedView}
          isGridHeader={isGridHeader}
          isLastCell={isLastCell}
          shouldShowIcon={shouldShowIcon && isMoreColumnsAvailable}
        >
          <CellPrimaryValue
            shouldShowIcon={shouldShowIcon}
            isGridHeader={isGridHeader}
            isLastCell={isLastCell}
            isSectionHeader={isSectionHeader}
            isNetWorthRow={isNetWorthRow}
            isTextCell={isTextCell}
            category={category}
            isArchived={isArchived}
            isExpandedView={isExpandedView}
          >
            {isPercentageCell
              ? cell.actualValue === 0
                ? isSectionHeader
                  ? `0%`
                  : `---`
                : isNetWorthRow ||
                  category === "categoryHeader" ||
                  (category === "header" && cell.selectedChartOptions === recapChartOptions.NETWORTH.id)
                ? formattedValue
                : cell.value === 0
                ? `~0.01%`
                : `${formatNumberWithKuberaNumberFormatSettingsSelector(store.getState(), cell.value)}%`
              : isGridHeader
              ? cell.value
              : formattedValue}
          </CellPrimaryValue>
          {secondaryValue && (
            <CellSecondaryValue isTextCell={isTextCell}>{showCellSecondaryValue(isDebtSection)}</CellSecondaryValue>
          )}
        </CellItem>
      );
    }
  };

  return (
    <CellContainer
      isTextCell={isTextCell}
      isGridHeader={isGridHeader}
      isLastCell={isLastCell}
      isSectionHeader={isSectionHeader}
      isCurrencyCell={isCurrencyCell}
      isFirstCell={isFirstCell}
      id={`${cellId}`}
      isNetWorthRow={isNetWorthRow}
      showOverflowIndicator={showOverflowIndicator}
      isExpandedView={isExpandedView}
      onFocus={handleFocus}
      isChartsCell={isChartsCell}
      isExpandCollapseIconCell={isExpandCollapseIconCell}
      onClick={
        !isDisabled
          ? isChartsCell
            ? props.handleOpenChartsModalClick
            : isExpandCollapseIconCell
            ? () => {
                props.handleRecapExpandOrCollapse(
                  rowIndex,
                  cell.selectedChartOptions === recapChartOptions.SHEETS_AND_SECTIONS.id,
                  cell.selectedChartOptions,
                  isExpandedView
                );
              }
            : () => {
                if (!isGridHeader) {
                  localStorage.setItem("latestCellIndexes", `${0},${sectionIndex},${rowIndex},${cellIndex}`);
                }
              }
          : null
      }
      isDisabled={isDisabled}
      onKeyDown={handleKeyDown}
    >
      {renderCellContent()}
      {showExpandOrShowMoreIcon()}
    </CellContainer>
  );
};
export default React.memo(ColumnarChartCellComponent);
